import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import { loadRepoListCount, loadRepoListPage, loadRepoTagCatalog } from '@/server/functions/pages/repo-list';
import { getRepo } from '@/server/functions/repo';

type RepoListPageOpts = {
  tags?: string[];
  search?: string;
  sortBy?: 'name' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
};

const repoListQueryKey = (orgSlug: string, opts?: RepoListPageOpts) => ['repo-list-page', orgSlug, opts ?? {}] as const;

const repoListQueryFn =
  (orgSlug: string, opts?: RepoListPageOpts) =>
  ({ pageParam }: { pageParam: string | undefined }) =>
    loadRepoListPage({
      data: {
        orgSlug,
        tags: opts?.tags,
        search: opts?.search,
        cursor: pageParam,
        sortBy: opts?.sortBy,
        sortOrder: opts?.sortOrder,
      },
    });

export const repoListInfiniteQueryOptions = (orgSlug: string, opts?: RepoListPageOpts) =>
  infiniteQueryOptions({
    queryKey: repoListQueryKey(orgSlug, opts),
    queryFn: repoListQueryFn(orgSlug, opts),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });

// Keyed by filters only — the count is independent of sort and cursor, so
// sort toggles and Load more clicks reuse it instead of recounting the org.
export const repoCountQueryOptions = (orgSlug: string, opts?: Pick<RepoListPageOpts, 'tags' | 'search'>) =>
  queryOptions({
    queryKey: ['repo-count', orgSlug, { tags: opts?.tags, search: opts?.search }] as const,
    queryFn: () => loadRepoListCount({ data: { orgSlug, tags: opts?.tags, search: opts?.search } }),
  });

export const repoTagCatalogQueryOptions = (orgSlug: string) =>
  queryOptions({
    queryKey: ['repo-tag-catalog', orgSlug],
    queryFn: () => loadRepoTagCatalog({ data: { orgSlug } }),
    // Fetched on demand when the Labels panel opens; keep it warm briefly so
    // reopening the panel doesn't refetch.
    staleTime: 60_000,
  });

export const repoQueryOptions = (orgSlug: string, name: string) =>
  queryOptions({
    queryKey: ['repo', orgSlug, name],
    queryFn: () => getRepo({ data: { orgSlug, name } }),
  });
